import {
  MeetingProvider,
  createMicrophoneAudioTrack,
  useMeeting,
  useParticipant,
} from "@videosdk.live/react-sdk";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useGetConsultantProfileQuery } from "../../redux/rtk/apiRtk";
import { authToken } from "../../pages/APIToken";
import { useSocket } from "../../utils/Socket.Provider";
import useCallTimer from "../../pages/consultant/call/hooks/useCallTimer";
import useCallManager from "../../pages/consultant/call/hooks/useCallManager";

const CallBar = ({ isVisible = false, callData, onEndCall }) => {
  const { data: ConsultantProfileData } = useGetConsultantProfileQuery("");
  const meetingId = callData?.meetingId;
  const [customMicStream, setCustomMicStream] = useState(null);
  const [micTrackError, setMicTrackError] = useState(false);
  const [micTrackAttempt, setMicTrackAttempt] = useState(0);

  useEffect(() => {
    if (!isVisible || !meetingId) return undefined;

    let cancelled = false;
    let createdStream;
    setCustomMicStream(null);
    setMicTrackError(false);

    createMicrophoneAudioTrack({
      encoderConfig: "speech_standard",
      noiseConfig: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        createdStream = stream;
        setCustomMicStream(stream);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Unable to initialize VideoSDK microphone:", error);
        setMicTrackError(true);
      });

    return () => {
      cancelled = true;
      createdStream?.getTracks().forEach((track) => track.stop());
    };
  }, [isVisible, meetingId, micTrackAttempt]);

  if (!isVisible || !meetingId) return null;

  if (!customMicStream) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-[#8A0154] px-4 py-3 text-center text-sm text-white">
        {micTrackError ? (
          <>
            Microphone setup failed. Check microphone permissions and{" "}
            <button
              type="button"
              className="underline"
              onClick={() => setMicTrackAttempt((attempt) => attempt + 1)}
            >
              retry
            </button>
            .
          </>
        ) : (
          "Preparing microphone..."
        )}
      </div>
    );
  }

  return (
    <MeetingProvider
      config={{
        meetingId: meetingId,
        micEnabled: true,
        webcamEnabled: false,
        customMicrophoneAudioTrack: customMicStream,
        name: ConsultantProfileData?.name || "Consultant",
        maxParticipants: 2, // Limit to 2 participants for one-to-one call
        participantId: ConsultantProfileData?._id, // Use a unique ID to prevent duplicates
      }}
      token={authToken}
    >
      <div
        className={`fixed top-0 left-0 right-0 z-[9999] transform transition-all duration-500 ease-in-out ${
          isVisible
            ? "translate-y-0 opacity-100"
            : "-translate-y-full opacity-0"
        }`}
      >
        {isVisible && (
          <MeeetingContainer
            callData={callData}
            onEndCall={onEndCall}
            ConsultantProfileData={ConsultantProfileData}
          />
        )}
      </div>
    </MeetingProvider>
  );
};

export default CallBar;

// Component to handle remote participant audio for 1:1 call
const ParticipantAudio = ({ participantId }) => {
  const { micStream } = useParticipant(participantId);
  const audioRef = useRef(null);

  useEffect(() => {
    const audioElement = audioRef.current;

    if (audioElement && micStream?.track) {
      const newStream = new MediaStream([micStream.track]);
      audioElement.srcObject = newStream;

      // Listen for track ended event
      micStream.track.onended = () => {
        console.warn("Mic track ended unexpectedly");
        // Optionally, request a new track or notify the user
      };

      audioElement
        .play()
        .catch((err) => console.error("Error playing remote audio:", err));
    }

    return () => {
      if (audioElement) {
        try {
          audioElement.pause();
          audioElement.srcObject = null;
          console.log("audioclean---------");
        } catch (err) {
          console.error("Error cleaning up remote audio:", err);
        }
      }
    };
  }, [micStream]);

  return <audio ref={audioRef} autoPlay playsInline />;
};

const MeeetingContainer = ({ callData, onEndCall, ConsultantProfileData }) => {
  const { socket } = useSocket();
  const [joined, setJoined] = useState(null);

  const [isLeavingCall, setIsLeavingCall] = useState(false);

  // Call management hooks
  const { callDuration, formatDuration, startCallTimer, stopCallTimer } =
    useCallTimer();

  // Meeting hooks
  const {
    join,
    leave,
    toggleMic,
    end,

    localMicOn,
    participants,
  } = useMeeting({
    onMeetingJoined: () => {
      setJoined("JOINED");
    },
    onMeetingStateChanged: (status) => {
      console.log("Meeting state changed:", status.state);

      if (status?.state === "CLOSED" || status?.state === "DISCONNECTED") {
        if (handleEndCall) handleEndCall();
      }
    },
    onMeetingLeft: () => {
      console.log("Meeting left");

      // if (handleEndCall) handleEndCall();
    },
    onParticipantJoined: (participantId) => {
      console.log("Participant joined:", { participantId });
      console.log("Total participants now:", participants.size + 1);
      startCallTimer();
    },
    onParticipantLeft: (participantId) => {
      console.log("Participant left:", participantId);
      console.log("Total participants now:", participants.size);
    },
    onCharacterJoined: (character) => {
      console.log("Character joined:", character);
    },

    onError: (error) => {
      console.error("Meeting error:", error);
    },
  });

  // Audio reference for local participant (not needed but kept for consistency)
  const micRef = useRef(null);

  // Auto-join meeting when component mounts
  const joinMeeting = useCallback(() => {
    console.log("Joining .....");

    setJoined("JOINING");
    join();
  }, [join]);

  useEffect(() => {
    if (participants.size === 0 && !joined) {
      joinMeeting();
    }
  }, [participants.size, joinMeeting, joined]);

  // Handle ending the call
  const handleEndCall = useCallback(() => {
    console.log("Ending call...");

    setIsLeavingCall(true);

    // setJoined(null);
    // Clean up audio before ending the call
    if (micRef.current) {
      try {
        micRef.current.pause();
        if (micRef.current.srcObject) {
          const tracks = micRef.current.srcObject.getTracks();
          tracks.forEach((track) => {
            track.stop();
          });
          micRef.current.srcObject = null;
        }
        console.log("Audio cleaned up before ending call");
      } catch (error) {
        console.error("Error cleaning up audio before ending call:", error);
      }
    }

    // // Stop the timer
    stopCallTimer();
    // End the meeting
    if (end) end();

    setTimeout(() => {
      if (onEndCall) onEndCall(callDuration);
    }, 2000);
  }, [setIsLeavingCall, stopCallTimer, end, onEndCall, callDuration]);

  // Handle socket events for call management
  useEffect(() => {
    if (!socket) return;

    socket.on("end-call", handleEndCall);
    return () => {
      socket.off("end-call", handleEndCall);
    };
  }, [socket, handleEndCall]);

  // // Global cleanup effect when component unmounts
  // useEffect(() => {
  //   const micElement = micRef.current;

  //   return () => {
  //     // Clean up audio when component unmounts
  //     if (micElement) {
  //       try {
  //         micElement.pause();
  //         if (micElement.srcObject) {
  //           const tracks = micElement.srcObject.getTracks();
  //           tracks.forEach((track) => {
  //             track.stop();
  //           });
  //           micElement.srcObject = null;
  //         }
  //       } catch (error) {
  //         console.error("Error cleaning up audio on unmount:", error);
  //       }
  //     }
  //   };
  // }, []);

  const handleMuteToggle = () => {
    if (toggleMic) {
      toggleMic();
    }
  };

  // Show loading state while joining
  if (joined === "JOINING") {
    return (
      <div className="bg-gradient-to-r from-[#A40165] to-[#8A0154] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-white/60 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">Connecting...</span>
            </div>
          </div>
        </div>
        <div className="h-1 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
      </div>
    );
  }

  // Only show the call bar when joined
  if (joined !== "JOINED") {
    return null;
  }

  return (
    <>
      {/* Hidden audio element for local participant */}
      {/* <audio ref={micRef} autoPlay playsInline /> */}

      {/* Audio elements for remote participants */}
      {Array.from(participants.keys()).map((participantId) => (
        <ParticipantAudio key={participantId} participantId={participantId} />
      ))}

      <div className="bg-gradient-to-r from-[#A40165] to-[#8A0154] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left section - Call info */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                {/* Active call indicator with audio visualization */}
                <div
                  className={`w-3 h-3 rounded-full ${
                    localMicOn ? "bg-green-400 animate-pulse" : "bg-gray-400"
                  }`}
                ></div>
                <span className="text-sm font-medium">
                  {localMicOn ? "On Call" : "Call Active"}
                </span>
              </div>

              <div className="hidden sm:block w-px h-6 bg-white/30"></div>

              <div className="flex items-center space-x-2">
                {callData?.profilePic ? (
                  <img
                    src={callData?.profilePic}
                    alt="Profile"
                    className="h-8 w-8 rounded-full"
                  />
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}

                <span className="text-sm text-white font-medium truncate max-w-32 sm:max-w-none">
                  {callData?.name || "Unknown"}
                </span>
              </div>
            </div>

            {/* Center section - Timer */}
            <div className="flex items-center justify-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1">
                <span className="text-lg font-mono font-semibold">
                  {formatDuration(callDuration)}
                </span>
              </div>
            </div>

            {/* Right section - Controls */}
            <div className="flex items-center space-x-2">
              {/* Mute/Unmute Button */}
              <button
                onClick={handleMuteToggle}
                className={`p-2 rounded-full transition-all duration-200 hover:scale-105 ${
                  !localMicOn
                    ? "bg-red-500 hover:bg-red-600"
                    : "bg-white/20 hover:bg-white/30"
                }`}
                title={!localMicOn ? "Unmute" : "Mute"}
              >
                {!localMicOn ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.793L4.828 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.828l3.555-3.793a1 1 0 011.617.793zM12 8a1 1 0 012 0v4a1 1 0 11-2 0V8z"
                      clipRule="evenodd"
                    />
                    <path d="M16.707 9.293a1 1 0 010 1.414l-1.414 1.414a1 1 0 01-1.414-1.414L15.293 10l-1.414-1.414a1 1 0 111.414-1.414l1.414 1.414z" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.793L4.828 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.828l3.555-3.793a1 1 0 011.617.793zM12 8a1 1 0 012 0v4a1 1 0 11-2 0V8z"
                      clipRule="evenodd"
                    />
                    <path d="M14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" />
                  </svg>
                )}
              </button>

              {/* End Call Button */}
              <button
                // onClick={handleEndCall}
                onClick={() => {
                  // Stop the timer
                  stopCallTimer();
                  // End the meeting
                  // if (leave) leave();
                  if (end) end();
                  const userId = callData?.fromUser;
                  const meetingId = callData?.meetingId;

                  if (socket && ConsultantProfileData?._id) {
                    console.log("ending call calling ....");

                    socket.emit("end-call", {
                      toUser: userId,
                      fromUser: ConsultantProfileData._id,
                      consultantid: ConsultantProfileData._id,
                      user: ConsultantProfileData._id,
                      meetingId: meetingId,
                    });
                  }
                }}
                className="bg-red-500 hover:bg-red-600 p-2 rounded-full transition-all duration-200 hover:scale-105 shadow-lg"
                title="End Call"
                disabled={isLeavingCall}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 rotate-135"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Bottom border with subtle animation */}
        <div className="h-1 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
      </div>
    </>
  );
};
